import { useMemo } from 'react';
import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import { BaseResponse } from '@utils/types/shared/base';
import { UseOrgsDropdownResult } from '@components/shared/org-switcher/org-switcher';
import { useRoles } from '@utils/hooks/use-roles';

// Reseller transcript-access levels (mirror of the backend enum). Only a
// customer that granted READWRITE may be switched into; lower levels 403.
const RESELLER_TRANSCRIPT_ACCESS_READWRITE = 3;

// Shape returned by GET /v3/reseller/customer — matches ResellerCustomerObject
// on the backend. Only the fields the dropdown consumes are typed here.
interface ResellerCustomerListItem {
  id: number;
  name: string;
  email: string;
  is_active: boolean;
  reseller_transcript_access: number;
}

// Fetches the reseller's own sub-customers for the switcher dropdown.
// Server-side filter is by name only (the BE endpoint doesn't expose the
// SYSOP q-grammar), so we use a simple `?q=name:ilike:<value>` and let
// the BE decide how to interpret it — falling back to client-side filter.
export function useResellerCustomersDropdown(
  q: string,
  enabled: boolean
): UseOrgsDropdownResult {
  // SYSOPs bypass the READWRITE transcript-access gate (the backend lets them
  // switch into any sub-customer), so every customer is switchable for them.
  const { isSysOp } = useRoles();
  const query = useQuery<BaseResponse<ResellerCustomerListItem[]>>(
    ['reseller-customers-dropdown', q],
    async () => {
      const params = new URLSearchParams({ offset: '0', limit: '50' });
      const { data } = await Axios.get<
        BaseResponse<ResellerCustomerListItem[]>
      >(`${URL_EXTENSION}/reseller/customer?${params.toString()}`);
      return data;
    },
    { staleTime: 30_000, keepPreviousData: true, enabled }
  );

  const options = useMemo(() => {
    const all = query.data?.result ?? [];
    const filtered = q
      ? all.filter(c => {
          const trimmed = q.trim().toLowerCase();
          if (/^\d+$/.test(trimmed)) return String(c.id) === trimmed;
          return c.name.toLowerCase().includes(trimmed);
        })
      : all;
    return filtered.map(c => ({
      id: c.id,
      label: c.name,
      value: c.id,
      email: c.email,
      isActive: c.is_active !== false,
      // Switching in grants full read/write, so the backend only allows it for
      // customers that granted READWRITE transcript access — unless the caller
      // is a SYSOP, who may switch into any sub-customer.
      switchable:
        isSysOp ||
        c.reseller_transcript_access === RESELLER_TRANSCRIPT_ACCESS_READWRITE
    }));
  }, [query.data, q, isSysOp]);

  return {
    options,
    isLoading: query.isLoading,
    isFetching: query.isFetching
  };
}
